import { useEffect, useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import type { ChatMessage, PromptDetail, PromptListItem, TestResult } from './types';

export interface SavePromptPayload {
  config: Record<string, unknown>;
  labels: string[];
  name: string;
  prompt: string | ChatMessage[];
  type: 'text' | 'chat';
}

const useLangfusePrompts = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [prompts, setPrompts] = useState<PromptListItem[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptDetail | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  useEffect(() => {
    Axios.get<PromptListItem[]>(Routes.list_admin_langfuse_prompts_path())
      .then(res => setPrompts(res.data))
      .catch(() => setError('Failed to load prompts.'))
      .finally(() => setIsLoadingList(false));
  }, []);

  const loadPrompt = (name: string, version?: number) => {
    setTestResult(null);
    setError(null);
    setIsLoadingPrompt(true);
    const params = version ? { name, version } : { name };
    Axios.get<PromptDetail>(Routes.show_prompt_admin_langfuse_prompts_path(), { params })
      .then(res => setSelectedPrompt(res.data))
      .catch(() => setError(`Failed to load prompt "${name}".`))
      .finally(() => setIsLoadingPrompt(false));
  };

  // Resolves true on success so the editor can stay open (with the edits) when a save fails.
  const savePromptVersion = (payload: SavePromptPayload): Promise<boolean> => {
    setIsSaving(true);
    setError(null);
    return Axios.post<PromptDetail>(Routes.admin_langfuse_prompts_path(), { langfuse_prompt: payload })
      .then((res) => {
        setSelectedPrompt(res.data);
        setPrompts(prev => prev.map(p => (
          p.name === payload.name ? { ...p, versions: [...p.versions, res.data.version] } : p
        )));
        return true;
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to save prompt.');
        return false;
      })
      .finally(() => setIsSaving(false));
  };

  const testPrompt = (name: string, variables: Record<string, string>, version?: number) => {
    setIsTesting(true);
    setTestResult(null);
    setError(null);
    const params: Record<string, unknown> = { name, variables };
    if (version) params.version = version;
    return Axios.post<TestResult>(Routes.test_admin_langfuse_prompts_path(), params)
      .then(res => setTestResult(res.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to run test.'))
      .finally(() => setIsTesting(false));
  };

  return {
    error,
    isLoadingList,
    isLoadingPrompt,
    isSaving,
    isTesting,
    loadPrompt,
    prompts,
    savePromptVersion,
    selectedPrompt,
    testPrompt,
    testResult,
  };
};

export default useLangfusePrompts;
